import axios from "axios";

// The website is a pure CLIENT of the remote Richi£Rich backend. The backend URL
// normally comes from REACT_APP_BACKEND_URL, but the platform keeps resetting that
// to the frontend's OWN domain (which has no /api), breaking every call. To stay
// resilient we fall back to the known remote backend whenever the configured value
// is missing or accidentally points at the site's own origin.
const REMOTE_BACKEND_FALLBACK = "https://richie-competitions.emergent.host";

function resolveBackendUrl(raw) {
  if (!raw) return REMOTE_BACKEND_FALLBACK;
  try {
    const u = new URL(raw);
    // If the backend URL points at the same host serving this app, it's the bug — use the real API.
    if (typeof window !== "undefined" && u.hostname === window.location.hostname) {
      return REMOTE_BACKEND_FALLBACK;
    }
    return raw.replace(/\/+$/, ""); // strip any trailing slash
  } catch {
    return REMOTE_BACKEND_FALLBACK;
  }
}

const BACKEND_URL = resolveBackendUrl(process.env.REACT_APP_BACKEND_URL);
export const API = `${BACKEND_URL}/api`;
export const TOKEN_KEY = "rrc_token";

const api = axios.create({ baseURL: API });

// Decode a JWT's exp (ms) without any dependency. Returns null if unknown.
export const getTokenExpiry = (t) => {
  try {
    const part = t.split(".")[1];
    const json = JSON.parse(atob(part.replace(/-/g, "+").replace(/_/g, "/")));
    return json && json.exp ? json.exp * 1000 : null;
  } catch {
    return null;
  }
};

export const isTokenExpired = (t) => {
  if (!t) return true;
  const exp = getTokenExpiry(t);
  return exp ? Date.now() >= exp : false;
};

// Clear the session and let the app react (used on expiry / 401).
export const forceLogout = () => {
  if (localStorage.getItem(TOKEN_KEY)) localStorage.removeItem(TOKEN_KEY);
  if (typeof window !== "undefined") window.dispatchEvent(new Event("auth:logout"));
};

api.interceptors.request.use((config) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) {
    // Never send an expired token; drop the session instead.
    if (isTokenExpired(t)) {
      forceLogout();
    } else {
      config.headers.Authorization = `Bearer ${t}`;
    }
  }
  return config;
});

// On any auth failure, kill the stale/invalid token so it can't linger.
api.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error?.response?.status === 401 && localStorage.getItem(TOKEN_KEY)) {
      forceLogout();
    }
    return Promise.reject(error);
  }
);

export const getErr = (e, fallback = "Something went wrong") => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(", ");
  if (d && typeof d === "object") return JSON.stringify(d);
  return e?.message || fallback;
};

export default api;
